import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignAPI, departmentAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPE_CONFIG = {
  recruitment:   { label: 'Tuyển dụng',    color: '#2563eb', bg: '#dbeafe' },
  training:      { label: 'Đào tạo',        color: '#7c3aed', bg: '#ede9fe' },
  engagement:    { label: 'Gắn kết',        color: '#059669', bg: '#d1fae5' },
  communication: { label: 'Truyền thông',   color: '#d97706', bg: '#fef3c7' },
  other:         { label: 'Khác',           color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_CONFIG = {
  planning:  { label: 'Lên kế hoạch',      color: '#64748b', bg: '#f1f5f9' },
  active:    { label: 'Đang triển khai',    color: '#0891b2', bg: '#cffafe' },
  completed: { label: 'Hoàn thành',         color: '#16a34a', bg: '#dcfce7' },
  cancelled: { label: 'Đã huỷ',             color: '#dc2626', bg: '#fee2e2' },
};

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 7,
  border: '1px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box',
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

const EMPTY = {
  title: '', type: 'recruitment', description: '', objectives: '', results: '',
  department_id: '', status: 'planning', start_date: '', end_date: '',
  budget: '', target_count: '', owner_id: '',
};

function Badge({ value, config }) {
  const c = config[value] || config.other || config.planning;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 12px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, color: c.color, background: c.bg,
    }}>{c.label}</span>
  );
}

function DetailItem({ label, value, icon }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
        {icon && <span>{icon}</span>}{label}
      </div>
      <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'hr_manager'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const [campaign, setCampaign] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');

  const [editModal, setEditModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      campaignAPI.get(id),
      departmentAPI.list(),
      employeeAPI.list({ limit: 200 }),
    ])
      .then(([cRes, dRes, eRes]) => {
        setCampaign(cRes.data.data);
        setDepartments(dRes.data.data);
        setEmployees(eRes.data.data || []);
      })
      .catch(() => setError('Không tìm thấy chiến dịch'))
      .finally(() => setLoading(false));
  }, [id]);

  const openEdit = () => {
    const c = campaign;
    setForm({
      title: c.title || '',
      type: c.type || 'other',
      description: c.description || '',
      objectives: c.objectives || '',
      results: c.results || '',
      department_id: c.department_id || '',
      status: c.status || 'planning',
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      end_date: c.end_date ? c.end_date.slice(0, 10) : '',
      budget: c.budget || '',
      target_count: c.target_count || '',
      owner_id: c.owner_id || '',
    });
    setFormError('');
    setEditModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setFormError('Tên chiến dịch không được để trống'); return; }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      setFormError('Ngày bắt đầu phải trước ngày kết thúc');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await campaignAPI.update(id, {
        ...form,
        department_id: form.department_id || null,
        owner_id: form.owner_id || null,
        budget: form.budget || 0,
        target_count: form.target_count || 0,
      });
      const res = await campaignAPI.get(id);
      const updated = res.data.data;
      setCampaign(updated);
      setEditModal(false);
      navigate(`/campaigns/${id}/${slugify(updated.title)}`, { replace: true });
    } catch (e) {
      setFormError(e.response?.data?.message || 'Lỗi lưu dữ liệu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bạn có chắc muốn xoá chiến dịch này?')) return;
    try {
      await campaignAPI.delete(id);
      navigate('/campaigns');
    } catch (e) {
      setError(e.response?.data?.message || 'Không thể xoá');
    }
  };

  const renderLines = (text, emptyMsg, color) => {
    if (!text?.trim()) return (
      <div style={{ color: '#94a3b8', fontSize: 14, fontStyle: 'italic', padding: '12px 0' }}>{emptyMsg}</div>
    );
    return text.split('\n').filter(l => l.trim()).map((line, i) => (
      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
        <span style={{ color, fontWeight: 700, flexShrink: 0, marginTop: 3 }}>•</span>
        <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.7 }}>{line.replace(/^[-•*]\s*/, '')}</span>
      </div>
    ));
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748b' }}>
      Đang tải...
    </div>
  );

  if (error || !campaign) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>😞</div>
      <div style={{ fontSize: 15, color: '#64748b', marginBottom: 20 }}>{error || 'Không tìm thấy chiến dịch'}</div>
      <button onClick={() => navigate('/campaigns')} style={{
        padding: '8px 20px', borderRadius: 8, border: 'none',
        background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', cursor: 'pointer', fontWeight: 600,
      }}>← Quay lại danh sách</button>
    </div>
  );

  const typeCfg   = TYPE_CONFIG[campaign.type]   || TYPE_CONFIG.other;
  const statusCfg = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.planning;
  const daysLeft  = campaign.end_date ? Math.ceil((new Date(campaign.end_date) - new Date()) / 86400000) : null;

  const tabs = [
    { key: 'overview',   label: '📋 Tổng quan' },
    { key: 'objectives', label: '🎯 Mục tiêu' },
    { key: 'results',    label: '📊 Kết quả' },
  ];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: '#64748b' }}>
        <button onClick={() => navigate('/campaigns')} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1',
          fontWeight: 600, fontSize: 13, padding: 0,
        }}>← Chiến dịch</button>
        <span>/</span>
        <span style={{ color: '#1e293b', fontWeight: 500 }}>{campaign.title}</span>
      </div>

      {/* Header card */}
      <div style={{
        background: '#fff', borderRadius: 14,
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        overflow: 'hidden', marginBottom: 20,
      }}>
        {/* Color strip */}
        <div style={{ height: 5, background: `linear-gradient(90deg, ${typeCfg.color}, ${typeCfg.color}66)` }} />

        <div style={{ padding: '24px 28px 0' }}>
          {/* Top row: badges + actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Badge value={campaign.type} config={TYPE_CONFIG} />
              <Badge value={campaign.status} config={STATUS_CONFIG} />
              {campaign.status === 'active' && daysLeft !== null && (
                <span style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  color: daysLeft < 0 ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#16a34a',
                  background: daysLeft < 0 ? '#fee2e2' : daysLeft <= 7 ? '#fef3c7' : '#dcfce7',
                }}>
                  {daysLeft < 0 ? `Quá hạn ${Math.abs(daysLeft)} ngày` : daysLeft === 0 ? 'Kết thúc hôm nay' : `Còn ${daysLeft} ngày`}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {canEdit && (
                <button onClick={openEdit} style={{
                  padding: '7px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
                  background: '#fff', cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600,
                }}>✏️ Chỉnh sửa</button>
              )}
              {canDelete && (
                <button onClick={handleDelete} style={{
                  padding: '7px 18px', borderRadius: 8, border: '1px solid #fca5a5',
                  background: '#fff', cursor: 'pointer', fontSize: 13, color: '#dc2626', fontWeight: 600,
                }}>🗑️ Xoá</button>
              )}
            </div>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 20px', lineHeight: 1.3 }}>
            {campaign.title}
          </h1>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #f1f5f9' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: tab === t.key ? 700 : 400,
                color: tab === t.key ? typeCfg.color : '#64748b',
                borderBottom: `2px solid ${tab === t.key ? typeCfg.color : 'transparent'}`,
                marginBottom: -2, transition: 'all 0.15s',
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div style={{ padding: '24px 28px 28px' }}>

          {/* ── Tổng quan ── */}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {campaign.description && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 18px', fontSize: 14, color: '#475569', lineHeight: 1.75 }}>
                  {campaign.description}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <DetailItem label="Phòng ban" value={campaign.department_name || 'Toàn công ty'} icon="🏬" />
                <DetailItem label="Người phụ trách" value={campaign.owner_name || '—'} icon="👤" />
                <DetailItem label="Ngày bắt đầu" value={fmtDate(campaign.start_date)} icon="📅" />
                <DetailItem label="Ngày kết thúc" value={fmtDate(campaign.end_date)} icon="🏁" />
                <DetailItem label="Ngân sách" value={campaign.budget > 0 ? `${fmt(campaign.budget)} đ` : '—'} icon="💰" />
                <DetailItem label="Mục tiêu số lượng" value={campaign.target_count > 0 ? `${campaign.target_count} người` : '—'} icon="🎯" />
                <DetailItem label="Tạo bởi" value={campaign.created_by_name || '—'} icon="✍️" />
                <DetailItem label="Ngày tạo" value={campaign.created_at ? new Date(campaign.created_at).toLocaleDateString('vi-VN') : '—'} icon="🗓️" />
              </div>

              {(campaign.objectives || campaign.results) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {campaign.objectives && (
                    <div onClick={() => setTab('objectives')} style={{
                      background: `${typeCfg.color}08`, border: `1px solid ${typeCfg.color}20`,
                      borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                      transition: 'box-shadow 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: typeCfg.color, marginBottom: 6 }}>🎯 Mục tiêu</div>
                      <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {campaign.objectives}
                      </div>
                      <div style={{ fontSize: 11, color: typeCfg.color, marginTop: 8, fontWeight: 600 }}>Xem chi tiết →</div>
                    </div>
                  )}
                  {campaign.results && (
                    <div onClick={() => setTab('results')} style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                      transition: 'box-shadow 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginBottom: 6 }}>📊 Kết quả</div>
                      <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {campaign.results}
                      </div>
                      <div style={{ fontSize: 11, color: '#16a34a', marginTop: 8, fontWeight: 600 }}>Xem chi tiết →</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Mục tiêu ── */}
          {tab === 'objectives' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: `${typeCfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎯</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Mục tiêu chiến dịch</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>Những gì cần đạt được khi kết thúc</div>
                </div>
              </div>

              {campaign.target_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                  <span style={{ fontSize: 20 }}>👥</span>
                  <span style={{ fontSize: 14, color: '#475569' }}>Số lượng mục tiêu:</span>
                  <strong style={{ fontSize: 16, color: typeCfg.color }}>{campaign.target_count} người</strong>
                </div>
              )}

              <div style={{ background: '#fafafa', borderRadius: 12, padding: '18px 20px', borderLeft: `4px solid ${typeCfg.color}` }}>
                {renderLines(campaign.objectives, 'Chưa có mục tiêu nào được đặt ra. Nhấn "Chỉnh sửa" để thêm.', typeCfg.color)}
              </div>
            </div>
          )}

          {/* ── Kết quả ── */}
          {tab === 'results' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📊</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Kết quả thực tế</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>Những gì đã đạt được sau triển khai</div>
                </div>
              </div>

              {campaign.status !== 'completed' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                  ⏳ Chiến dịch chưa hoàn thành — kết quả có thể được cập nhật trong quá trình thực hiện.
                </div>
              )}
              {campaign.status === 'completed' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
                  ✅ Chiến dịch đã hoàn thành.
                </div>
              )}

              <div style={{ background: '#fafafa', borderRadius: 12, padding: '18px 20px', borderLeft: '4px solid #22c55e' }}>
                {renderLines(campaign.results, 'Chưa có kết quả nào được ghi nhận. Nhấn "Chỉnh sửa" để cập nhật.', '#22c55e')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={e => { if (e.target === e.currentTarget) setEditModal(false); }}>
          <div style={{
            background: '#fff', borderRadius: 14, width: '100%', maxWidth: 580,
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{
              padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              position: 'sticky', top: 0, background: '#fff', zIndex: 1,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Chỉnh sửa chiến dịch</div>
              <button onClick={() => setEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Tên chiến dịch *</label>
                <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Loại chiến dịch</label>
                  <select style={inputStyle} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Trạng thái</label>
                  <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Phòng ban phụ trách</label>
                <select style={inputStyle} value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
                  <option value="">Toàn công ty</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Người phụ trách</label>
                <select style={inputStyle} value={form.owner_id} onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))}>
                  <option value="">— Chọn nhân viên —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Ngày bắt đầu</label>
                  <input type="date" style={inputStyle} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Ngày kết thúc</label>
                  <input type="date" style={inputStyle} value={form.end_date} min={form.start_date || undefined} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Ngân sách (VNĐ)</label>
                  <input type="number" style={inputStyle} value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} placeholder="0" min={0} />
                  {form.budget > 0 && <div style={{ fontSize: 11, color: '#6366f1', marginTop: 3 }}>{fmt(form.budget)} đ</div>}
                </div>
                <div>
                  <label style={labelStyle}>Số lượng mục tiêu</label>
                  <input type="number" style={inputStyle} value={form.target_count} onChange={e => setForm(f => ({ ...f, target_count: e.target.value }))} placeholder="0" min={0} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Mô tả</label>
                <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>🎯 Mục tiêu chiến dịch</label>
                <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.objectives} onChange={e => setForm(f => ({ ...f, objectives: e.target.value }))} placeholder="Mô tả mục tiêu cụ thể cần đạt được..." />
              </div>
              <div>
                <label style={labelStyle}>📊 Kết quả thực tế</label>
                <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.results} onChange={e => setForm(f => ({ ...f, results: e.target.value }))} placeholder="Ghi nhận kết quả đạt được..." />
              </div>
              {formError && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>{formError}</div>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={() => setEditModal(false)} style={{ padding: '8px 20px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Huỷ</button>
                <button onClick={handleSubmit} disabled={submitting} style={{
                  padding: '8px 20px', borderRadius: 7, border: 'none',
                  background: submitting ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#4f46e5)',
                  color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
                }}>{submitting ? 'Đang lưu...' : 'Lưu'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
